import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { Clock, Play, RefreshCw, FileText, AlertCircle, CheckCircle, Settings } from 'lucide-react';

const CronConfigPage = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [selectedInterval, setSelectedInterval] = useState(2);
  const [executing, setExecuting] = useState(false);
  const [logs, setLogs] = useState('');
  const [logsLoading, setLogsLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const intervalOptions = [
    { value: 1, label: '1 hora (24 veces/día)' },
    { value: 2, label: '2 horas (12 veces/día) - Recomendado' },
    { value: 4, label: '4 horas (6 veces/día)' },
    { value: 6, label: '6 horas (4 veces/día)' },
    { value: 12, label: '12 horas (2 veces/día)' },
    { value: 24, label: '24 horas (1 vez/día)' }
  ];

  useEffect(() => {
    fetchConfig();
    fetchLogs();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/admin/cron-config');
      setConfig(response.data);
      setSelectedInterval(response.data.interval_hours);
    } catch (error) {
      console.error('Error obteniendo configuración:', error);
      setMessage({ type: 'error', text: 'Error al cargar configuración' });
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const response = await api.get('/admin/cron-logs?lines=100');
      setLogs(response.data.logs);
    } catch (error) {
      console.error('Error obteniendo logs:', error);
      setLogs('Error al cargar logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleUpdateInterval = async () => {
    try {
      setLoading(true);
      const response = await api.put(`/admin/cron-config?interval_hours=${selectedInterval}`);
      setMessage({ 
        type: 'success', 
        text: response.data.cron_updated 
          ? `Configuración actualizada: cron cada ${selectedInterval} horas`
          : `Configuración guardada. ${response.data.message}`
      });
      fetchConfig();
    } catch (error) {
      console.error('Error actualizando configuración:', error);
      setMessage({ type: 'error', text: 'Error al actualizar configuración' });
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteNow = async () => {
    try {
      setExecuting(true);
      setMessage({ type: 'info', text: 'Ejecutando cron... esto puede tomar unos segundos' });
      
      const response = await api.post('/admin/cron-execute-now');
      
      setMessage({ 
        type: 'success', 
        text: `Ejecución completada en ${response.data.execution_time_seconds?.toFixed(2)} segundos`
      });
      
      // Recargar logs después de ejecutar
      setTimeout(() => {
        fetchLogs();
        fetchConfig();
      }, 1000);
      
    } catch (error) {
      console.error('Error ejecutando cron:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Error al ejecutar cron' });
    } finally {
      setExecuting(false);
    }
  };

  const getExecutionSchedule = (hours) => {
    const times = [];
    for (let i = 0; i < 24; i += hours) {
      times.push(`${i.toString().padStart(2, '0')}:00`);
    }
    return times.join(', ');
  };

  if (loading && !config) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} p-6`}>
        <div className="flex justify-center items-center h-64">
          <RefreshCw className="animate-spin h-8 w-8 text-blue-500" />
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} p-6`}>
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Settings className={`h-8 w-8 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Configuración de Suspensión Automática
            </h1>
          </div>
          <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Gestiona el cron de suspensión de clientes PPPoE vencidos
          </p>
        </div>

        {/* Mensaje */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
            message.type === 'success' ? 'bg-green-100 border border-green-300' :
            message.type === 'error' ? 'bg-red-100 border border-red-300' :
            'bg-blue-100 border border-blue-300'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
            ) : (
              <AlertCircle className={`h-5 w-5 mt-0.5 ${
                message.type === 'error' ? 'text-red-600' : 'text-blue-600'
              }`} />
            )}
            <p className={`flex-1 ${
              message.type === 'success' ? 'text-green-800' :
              message.type === 'error' ? 'text-red-800' :
              'text-blue-800'
            }`}>
              {message.text}
            </p>
            <button
              onClick={() => setMessage(null)}
              className="text-gray-500 hover:text-gray-700"
            >
              ×
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Panel de Configuración */}
          <div className="lg:col-span-2">
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-6`}>
              
              {/* Configuración de Intervalo */}
              <div className="mb-6">
                <h2 className={`text-xl font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <Clock className="h-5 w-5" />
                  Frecuencia de Ejecución
                </h2>
                
                <div className="space-y-3">
                  {intervalOptions.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition ${
                        selectedInterval === option.value
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : `border-gray-300 dark:border-gray-600 ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`
                      }`}
                    >
                      <input
                        type="radio"
                        name="interval"
                        value={option.value}
                        checked={selectedInterval === option.value}
                        onChange={(e) => setSelectedInterval(parseInt(e.target.value))}
                        className="mr-3 h-4 w-4"
                      />
                      <span className={`flex-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        {option.label}
                      </span>
                      {option.value === 2 && (
                        <span className="text-xs bg-green-500 text-white px-2 py-1 rounded">
                          Recomendado
                        </span>
                      )}
                    </label>
                  ))}
                </div>

                {selectedInterval && (
                  <div className={`mt-4 p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'} mb-2`}>
                      <strong>Horarios de ejecución:</strong>
                    </p>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} font-mono`}>
                      {getExecutionSchedule(selectedInterval)}
                    </p>
                  </div>
                )}

                <button
                  onClick={handleUpdateInterval}
                  disabled={loading || selectedInterval === config?.interval_hours}
                  className={`mt-4 w-full py-3 rounded-lg font-semibold transition flex items-center justify-center gap-2 ${
                    selectedInterval === config?.interval_hours
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {loading ? (
                    <>
                      <RefreshCw className="animate-spin h-5 w-5" />
                      Actualizando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-5 w-5" />
                      Guardar Configuración
                    </>
                  )}
                </button>
              </div>

              {/* Ejecución Manual */}
              <div className="border-t border-gray-300 dark:border-gray-600 pt-6">
                <h2 className={`text-xl font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <Play className="h-5 w-5" />
                  Ejecución Manual
                </h2>
                
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                  Ejecuta el proceso de suspensión inmediatamente sin esperar al próximo intervalo programado.
                </p>

                <button
                  onClick={handleExecuteNow}
                  disabled={executing}
                  className={`w-full py-3 rounded-lg font-semibold transition flex items-center justify-center gap-2 ${
                    executing
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-orange-600 text-white hover:bg-orange-700'
                  }`}
                >
                  {executing ? (
                    <>
                      <RefreshCw className="animate-spin h-5 w-5" />
                      Ejecutando...
                    </>
                  ) : (
                    <>
                      <Play className="h-5 w-5" />
                      Ejecutar Ahora
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Panel de Estado */}
          <div className="space-y-6">
            
            {/* Estado Actual */}
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-6`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Estado Actual
              </h3>
              
              <div className="space-y-3">
                <div>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Intervalo:</p>
                  <p className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {config?.interval_hours 
                      ? `Cada ${config.interval_hours} hora${config.interval_hours !== 1 ? 's' : ''}`
                      : 'Cargando...'
                    }
                  </p>
                </div>
                
                <div>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Estado:</p>
                  <p className="text-lg font-semibold text-green-600">
                    ● Activo
                  </p>
                </div>
                
                {config?.last_execution && (
                  <div>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Última ejecución:</p>
                    <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {new Date(config.last_execution).toLocaleString('es-MX')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Información */}
            <div className={`${isDark ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'} border rounded-lg p-4`}>
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-800'} font-semibold mb-1`}>
                    ¿Cómo funciona?
                  </p>
                  <p className={`text-xs ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
                    El cron revisa automáticamente todos los clientes PPPoE activos y suspende aquellos que hayan vencido + días de gracia.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Logs */}
        <div className={`mt-6 ${isDark ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg p-6`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-xl font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <FileText className="h-5 w-5" />
              Logs de Ejecución
            </h2>
            <button
              onClick={fetchLogs}
              disabled={logsLoading}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
            >
              <RefreshCw className={`h-4 w-4 ${logsLoading ? 'animate-spin' : ''}`} />
              Actualizar
            </button>
          </div>
          
          <div className={`${isDark ? 'bg-gray-900' : 'bg-gray-100'} rounded-lg p-4 max-h-96 overflow-y-auto`}>
            <pre className={`text-xs font-mono whitespace-pre-wrap ${isDark ? 'text-green-400' : 'text-gray-800'}`}>
              {logsLoading ? 'Cargando logs...' : logs || 'No hay logs disponibles'}
            </pre>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CronConfigPage;
